import React, { ReactNode } from 'react'
import { css } from '@emotion/core'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { curry } from '@divvy-web/utils'

import CustomerAssistanceButton from '@sputnik/components/CustomerAssistanceButton/CustomerAssistanceButton'
import ReviewsSideSection from '@sputnik/components/ReviewsSideSection/ReviewsSideSection'
import { PAGE_NAME_STRINGS } from '@sputnik/resources/constants'

import DashboardHeading from './DashboardHeading'

interface PageViewWrapperProps {
  bdcOrgId?: string
  canStartNewApplication?: boolean
  children: ReactNode
  dsaVersion?: string
  inviteCollaborators?: boolean
  pageName: string
}

const PageViewWrapper = ({
  bdcOrgId,
  canStartNewApplication = false,
  children,
  dsaVersion = '',
  inviteCollaborators,
  pageName,
}: PageViewWrapperProps) => {
  const {
    PAGE_MIGRATION_SUCCESS,
    PAGE_DIVVY_PROGRAM_FIT,
    PAGE_DASHBOARD,
    PAGE_REVIEW_AND_SIGN,
    PAGE_APPLICATION_SUBMITTED,
  } = PAGE_NAME_STRINGS
  const isMigrationSuccess = pageName === PAGE_MIGRATION_SUCCESS
  const isDivvyProgramFitPage = pageName === PAGE_DIVVY_PROGRAM_FIT
  const isDashboard = pageName === PAGE_DASHBOARD
  const isReviewAndSign = pageName === PAGE_REVIEW_AND_SIGN
  const isApplicationSubmitted = pageName === PAGE_APPLICATION_SUBMITTED
  const [makeTestId] = useNameFormatter('PageViewWrapper')

  const shouldHaveOneColorWidth = isReviewAndSign

  if (isDashboard || isMigrationSuccess) {
    return (
      <div
        className='dashboard-wrapper'
        data-testid={makeTestId('')}
      >
        <CustomerAssistanceButton />
        <DashboardHeading
          bdcOrgId={bdcOrgId}
          canStartNewApplication={canStartNewApplication}
          isMigrationSuccess={isMigrationSuccess}
        />
        <div css={dashboardStyles}>{children}</div>
      </div>
    )
  }

  return (
    <div
      css={pageViewWrapperStyles({
        isApplicationSubmitted,
        isDivvyProgramFitPage,
        shouldHaveOneColorWidth,
      })}
    >
      <div className='main-page-wrapper'>
        <CustomerAssistanceButton />
        {children}
      </div>
      <div className='reviews-side-section-wrapper'>
        <ReviewsSideSection
          dsaVersion={dsaVersion}
          inviteCollaborators={inviteCollaborators}
          pageName={pageName}
        />
      </div>
    </div>
  )
}

export default PageViewWrapper

const pageViewWrapperStyles = curry(
  ({ isApplicationSubmitted, isDivvyProgramFitPage, shouldHaveOneColorWidth }, { mq }) => css`
    display: flex;
    min-height: 100vh;
    max-height: 100vh;
    gap: 0;
    ${mq.xSmallMaxWidth({ maxHeight: 'auto', minHeight: 'auto' })}

    .reviews-side-section-wrapper {
      width: ${shouldHaveOneColorWidth ? '35%' : isApplicationSubmitted || isDivvyProgramFitPage ? '40%' : '25%'};
      min-height: 100vh;
      overflow-y: auto;
      background-color: var(--tri-color-fill-accent-primary);

      ${!isApplicationSubmitted && mq.largeMaxWidth({ display: 'none' })};
      ${isApplicationSubmitted && mq.xSmallMaxWidth({ display: 'none' })};
    }

    .main-page-wrapper {
      z-index: 3;
      flex-grow: 2;
      ${mq.largeMaxWidth({ width: '100%' })};
      ${mq.xSmallMaxWidth({ overflow: 'visible', width: '100%' })}
    }
  `,
)

const dashboardStyles = ({ mq }) => css`
  padding-bottom: var(--tri-space-1200);
  margin: var(--tri-space-800) var(--tri-space-1500);

  ${mq.mediumMaxWidth({ margin: 'var(--tri-space-500)', paddingBottom: 'var(--tri-space-1500)' })}
`
